import { IAccount } from '@entities/accounts/model';
import { ICard } from '@entities/cards/model';
import { IClientProfile } from '@entities/client/model';
import { client } from '@shared/api';
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export enum ETransactionType {
  EXCHANGE = 'EXCHANGE',
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
  CARD = 'CARD',
  INVOICE = 'INVOICE',
  TRANSFER = 'TRANSFER',
  SYSTEM = 'SYSTEM',
}

export enum ETransactionStatus {
  COMPLETE = 'COMPLETE',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export enum EDirection {
  IN = 'in',
  OUT = 'out',
}

export interface IOperationAccount extends IAccount {
  client?: Pick<IClientProfile, 'id' | 'firstName' | 'lastName'>;
}

export interface IOperation {
  id: string;
  transactionId: string;
  type: ETransactionType;
  status: ETransactionStatus;
  cardId: string | null;
  merchantCode: string | null;
  merchantName: string | null;
  importedAt: string | null;
  dzingStatus: string | null;
  debitAccountId: string;
  debitAmount: string;
  debitAccountBalanceAfter: number;
  creditAccountId: string;
  creditAmount: string;
  externalAmount: number;
  externalCurrency: string;
  creditAccountBalanceAfter: number;
  createdAt: string;
  card: ICard | null;
  creditAccount: IOperationAccount;
  debitAccount: IOperationAccount;
  direction?: 'in' | 'out' | undefined;
  comment?: string | null;
  internalCurrency: string;
}

export interface IStatementAccount {
  account: {
    address: string;
    balance: number;
    client: {
      address: string;
      createdAt: string;
      defaultCurrency: string;
      email: string;
      firstName: string;
      id: string;
      isTelegramConnected: false;
      isWelcomed: false;
      lastName: string;
      notificationChannels: string[];
      phone: string;
      photoUrl: string;
      timezone: string;
    };
    createdAt: string;
    currency: string;
    id: string;
    name: string;
    type: string;
  };

  moneyIn: number;
  moneyOut: number;
}

export interface IStatement {
  accounts: IStatementAccount[];
  currency: string;
  moneyIn: number;
  moneyOut: number;
}

export interface ITransactionGroup {
  id: string;
  createdAt: string;
  operations: IOperation[];
}

export interface IPaginatedTransactions {
  results: ITransactionGroup[];
  statement: IStatement;
  total: number;
}

export interface ITransactions {
  results: ITransactionGroup[];
  statement: IStatement;
  total: number;
}

export interface IGetTransactionsParams {
  sort?: 'asc' | 'desc';
  skip?: number;
  take?: number;
  accountIds?: string[];
  currencies?: string[];
  statuses?: Array<ETransactionStatus>;
  cardIds?: string[];
  direction?: 'in' | 'out';
  fromDate?: string;
  toDate?: string;
  query?: string;
  types?: ETransactionType[];
  exportStatementInCurrency?: string;
}

export const useGetTransaction = (
  transactionId: string,
  options?: Omit<
    UseQueryOptions<ITransactionGroup, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const query = useQuery<ITransactionGroup, AxiosError>({
    queryKey: ['transaction', transactionId],
    queryFn: () =>
      client
        .get(`/client/transactions/${transactionId}`)
        .then((res) => res.data),
    ...options,
  });

  return {
    transaction: query.data,
    ...query,
  };
};

export const queryKeyGetTransactions = 'transactions';
export const queryKeyGetStatementTransactions = 'statement-transactions';
export const useGetTransactions = (
  params: IGetTransactionsParams = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      IPaginatedTransactions,
      AxiosError,
      InfiniteData<IPaginatedTransactions>,
      (string | IGetTransactionsParams)[],
      number
    >,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >,
) => {
  return useInfiniteQuery<
    IPaginatedTransactions,
    AxiosError,
    InfiniteData<IPaginatedTransactions>,
    (string | IGetTransactionsParams)[],
    number
  >({
    queryKey: [queryKeyGetTransactions, params],
    queryFn: ({ pageParam = 0 }) =>
      client
        .get('/client/transactions', {
          params: {
            ...params,
            skip: params.skip || pageParam * (params.take || 10),
          },
        })
        .then((res) => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (total, page) => total + page.results.length,
        0,
      );
      return loadedItems < lastPage.total ? allPages.length : undefined;
    },
    ...options,
  });
};

export const useGetOperation = (
  operationId: string,
  options?: Omit<
    UseQueryOptions<IOperation, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const query = useQuery<IOperation, AxiosError>({
    queryKey: ['operation', operationId],
    queryFn: () =>
      client.get(`/client/operations/${operationId}`).then((res) => res.data),
    ...options,
  });

  return {
    operation: query.data,
    ...query,
  };
};

export const useGetStatementTransactions = (
  params: IGetTransactionsParams = {},
  options?: Omit<
    UseQueryOptions<ITransactions, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<ITransactions, AxiosError>({
    queryKey: [queryKeyGetStatementTransactions, params],
    queryFn: () =>
      client
        .get('/client/transactions', {
          params: {
            ...params,
          },
        })
        .then((res) => res.data),

    ...options,
  });
};
